<template>
  <page-view :menu="menu">
    <template #header>
      <base-header :title="$t('message.settings')"> </base-header>
    </template>
    <template #content>
      <StripeRedirectionModal />
      <v-row no-gutters v-if="!showAllEvents">
        <v-col cols="12" class="mt-6">
          <v-row align="center" no-gutters>
            <span class="pageTitle">
              {{ $t('message.routesTitles.Account') }}
            </span>
          </v-row>
        </v-col>
        <v-col v-if="loadings.fetchData" cols="12" class="mt-14 mb-8">
          <loading-logo :value="200" />
        </v-col>
        <v-col v-else cols="12" class="mt-8">
          <v-row no-gutters>
            <v-col cols="4">
              <span class="categoriesTitle">{{
                $t('message.pages.organization.info')
              }}</span>
            </v-col>
            <v-col cols="8">
              <v-row
                no-gutters
                class="flex-column"
                justify="start"
                align="start"
              >
                <v-col cols="12" class="mb-2 categoriesContext textType--text">
                  Logo
                </v-col>
                <div v-if="editing === '' || isEditingName" class="grid">
                  <div class="grid__avatar">
                    <template v-if="loading">
                      <v-skeleton-loader
                        width="150"
                        height="150"
                        type="image"
                      />
                    </template>
                    <template v-else>
                      <img
                        v-if="hasAvatar"
                        class="grid__avatar--img"
                        :alt="organization.name"
                        :src="organization.image_path"
                      />
                      <v-avatar v-else class="grid__avatar--text" size="100%">
                        <span>
                          {{ userNameInitials }}
                        </span>
                      </v-avatar>
                      <div class="grid__avatar--ctrl" v-if="hasEditOrgPerm">
                        <label for="organizationAvatarImg">
                          <v-icon>mdi-camera</v-icon>
                        </label>
                        <v-divider v-if="hasAvatar" color="white" vertical />
                        <base-btn
                          v-if="hasAvatar"
                          type="tertiary"
                          icon="mdi-delete"
                          @click="deleteImage"
                        />
                      </div>
                    </template>
                  </div>
                </div>
                <!-- file -->
                <aside class="hidden">
                  <v-file-input
                    id="organizationAvatarImg"
                    accept="image/png, image/jpeg, image/jpg, image/gif"
                    @change="handleUpload"
                  />
                </aside>
                <v-col cols="12">
                  <v-divider class="mb-6" />
                </v-col>
                <v-col cols="12">
                  <v-row no-gutters>
                    <v-col class="flex-grow-1 flex-shrink-0">
                      <v-row v-if="!isEditingOrganizationName" no-gutters>
                        <v-col cols="12">
                          <span class="categoriesContext textType--text">
                            {{ $t('message.pages.organization.orgName') }}
                          </span>
                        </v-col>
                        <v-col cols="12">
                          <span class="subjectInfo textType2--text">{{
                            organization.name
                          }}</span>
                        </v-col>
                      </v-row>
                      <v-row class="flex-column" v-else>
                        <v-text-field
                          v-model="organization.name"
                          class="mb-2 pr-4"
                          outlined
                          counter="255"
                          maxlength="255"
                          dense
                          hide-details
                        ></v-text-field>
                        <span class="adviceOrganizationName textType2--text">
                          {{ $t('message.pages.organization.orgNameLimit') }}:
                          ({{ organization.name.length }}/255)</span
                        >
                      </v-row>
                    </v-col>
                    <v-col
                      class="flex-grow-0 flex-shrink-1"
                      style="min-width: 80px"
                    >
                      <v-row
                        v-if="hasEditOrgPerm"
                        no-gutters
                        align="center"
                        justify="end"
                      >
                        <v-btn
                          class="mt-2"
                          v-if="!isEditingOrganizationName"
                          icon
                          color="primary"
                          @click="isEditingOrganizationName = true"
                        >
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                        <div v-else>
                          <v-progress-circular
                            v-if="isFetchingUpdate"
                            class="mt-2 mr-4"
                            :size="20"
                            width="3"
                            color="primary"
                            indeterminate
                          />
                          <template v-else>
                            <v-btn
                              icon
                              color="primary"
                              :disabled="!canSaveName"
                              @click="handleUpdateOrganization(true)"
                            >
                              <v-icon>mdi-check</v-icon>
                            </v-btn>
                            <v-btn
                              icon
                              color="error"
                              @click="handleClearInput()"
                            >
                              <v-icon>mdi-close</v-icon>
                            </v-btn>
                          </template>
                        </div>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" class="mt-16" v-if="isBeta">
          <v-row no-gutters>
            <v-col cols="4">
              <span class="categoriesTitle">{{
                $t('message.pages.organization.events')
              }}</span>
            </v-col>
            <v-col v-if="loadingLogs">
              <loading-logo :value="200" />
            </v-col>
            <v-col v-else-if="reducedEvents.length">
              <v-row v-for="(event, i) in reducedEvents" :key="i" no-gutters>
                <v-col cols="8" class="filterPill">
                  <span>
                    {{ event.description }}
                  </span>
                </v-col>
                <v-col>
                  <v-row no-gutters align="center" justify="end">
                    <span class="filterPill">{{ formatDate(event.date) }}</span>
                  </v-row>
                </v-col>
                <v-col cols="12" class="py-2">
                  <v-divider />
                </v-col>
              </v-row>
            </v-col>
            <v-col v-else>
              <span class="caption">
                {{ $t('message.pages.organization.logMsg') }} <br />
                {{ $t('message.pages.organization.logMsg2') }}
                {{ `"${$t('message.pages.organization.seeAll')}".` }}
              </span>
            </v-col>
            <v-col cols="12" v-if="!loadingLogs">
              <v-row no-gutters>
                <v-spacer />
                <span
                  @click="handleShowAllEvents"
                  class="primary--text pointer"
                >
                  {{ $t('message.pages.organization.seeAll') }}
                </span>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" class="mt-12" v-if="isRootUser">
          <v-divider />
        </v-col>
        <v-col cols="12" class="mt-8" v-if="isRootUser">
          <v-row no-gutters>
            <v-col cols="4">
              <span class="categoriesTitle">{{
                $t('message.pages.organization.cancelAccount')
              }}</span>
            </v-col>
            <v-col cols="8">
              <v-row no-gutters>
                <span>{{ $t('message.pages.organization.cancelText') }}</span>
                <v-spacer />
                <v-btn
                  color="error"
                  :loading="crispLoading"
                  small
                  @click="goToStripe"
                >
                  {{ $t('message.actions.cancel') }}
                </v-btn>
              </v-row>
            </v-col>
            <v-col cols="12" v-if="errors.fetchError" class="mt-4">
              <v-row no-gutters align="center">
                <v-alert dense outlined type="error" width="100%">
                  <span class="footerText align-text d-block">
                    {{ errors.shownErrMsg }}
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon class="pb-1" size="18" v-bind="attrs" v-on="on">
                          mdi-help-circle
                        </v-icon>
                      </template>
                      <v-container
                        class="text-body-2 pa-0"
                        style="max-width: 230px"
                      >
                        {{ errors.tooltipErrMsg }}
                      </v-container>
                    </v-tooltip>
                  </span>
                </v-alert>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row no-gutters v-else>
        <v-col cols="12" class="mt-6 mb-4">
          <v-row no-gutters class="align-center">
            <v-icon class="mr-2 pointer" @click="handleLeaveFilters"
              >mdi-chevron-left</v-icon
            >
            <span class="mr-3">{{
              $t('message.pages.organization.filteredBy')
            }}</span>
            <date-picker
              :shortcuts="shortcuts"
              :clearable="false"
              :disabled-date="verifyDisabledDate"
              popup-class="organizationCalendar"
              v-model="dateRange"
              editable
              confirm
              range
            >
              <template v-slot:input>
                <v-card
                  class="px-2 py-1 rounded-pill filterPill mr-2"
                  flat
                  :style="
                    dateRange
                      ? 'border: 1px dashed #a0acff !important'
                      : 'border: 1px solid #a0acff !important'
                  "
                  style="cursor: pointer"
                >
                  <v-row no-gutters>
                    <span v-if="!dateRange" class="mr-1"> + </span>
                    {{ eventFilters.period.title }}
                    <span v-if="dateRange" class="ml-2">
                      <b>{{ dateRangeValue }}</b>
                    </span>
                    <v-spacer />
                    <v-icon
                      v-if="dateRange"
                      small
                      class="ml-1"
                      @click="resetDateRange"
                    >
                      mdi-close
                    </v-icon>
                  </v-row>
                </v-card>
              </template>
              <template v-slot:icon-calendar> <div></div> </template>
            </date-picker>
            <v-menu
              v-model="showMenu"
              :min-width="menuWidthOnMenuItem"
              :position-x="x"
              :position-y="y"
              absolute
              offset-y
              :transition="null"
              :close-on-content-click="false"
            >
              <v-list dense>
                <!-- <v-list-item v-if="menuItem.objectName === 'user'" class="my-2">
                  <v-text-field
                    dense
                    outlined
                    :label="'Buscar usuário'"
                    hide-details
                    prepend-inner-icon="mdi-magnify"
                  ></v-text-field>
                </v-list-item> -->
                <template v-if="menuItem.objectName === 'user'">
                  <v-list-item
                    v-for="(user, i) in users"
                    :key="i"
                    @click="handleSelectUser(user)"
                  >
                    <v-list-item-title class="textType2--text listItem">
                      {{ user.name }}
                    </v-list-item-title>
                  </v-list-item>
                </template>
                <template v-else-if="menuItem.objectName === 'action'">
                  <v-list-item
                    v-for="(action, i) in actions"
                    :key="i"
                    @click="handleSelectAction(action)"
                  >
                    <v-list-item-title class="textType2--text listItem">
                      {{ action.text }}
                    </v-list-item-title>
                  </v-list-item>
                </template>
                <template v-else-if="menuItem.objectName === 'group'">
                  <v-list-item
                    v-for="(group, i) in groups"
                    :key="i"
                    @click="handleSelectGroup(group)"
                  >
                    <v-list-item-title class="textType2--text listItem">
                      {{ group.text }}
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-list>
            </v-menu>
            <v-card
              class="px-2 py-1 rounded-pill filterPill mr-2"
              flat
              :style="
                eventFilters.user.content
                  ? 'border: 1px dashed #a0acff !important'
                  : 'border: 1px solid #a0acff !important'
              "
              @click="(e) => handleEventFilter(eventFilters.user, e)"
            >
              <span>
                <span v-if="!eventFilters.user.content" class="mr-1">+</span>
                {{ eventFilters.user.title }}
                <span v-if="eventFilters.user.content">
                  <b>{{ eventFilters.user.content }}</b>
                </span>
                <v-icon v-if="eventFilters.user.content" small class="ml-1">
                  mdi-close
                </v-icon>
              </span>
            </v-card>
            <v-card
              class="px-2 py-1 rounded-pill filterPill mr-2"
              flat
              :style="
                eventFilters.group.content
                  ? 'border: 1px dashed #a0acff !important'
                  : 'border: 1px solid #a0acff !important'
              "
              @click="(e) => handleEventFilter(eventFilters.group, e)"
            >
              <span>
                <span v-if="!eventFilters.group.content" class="mr-1">+</span>
                {{ eventFilters.group.title }}
                <span v-if="eventFilters.group.content">
                  <b>{{ eventFilters.group.content }}</b>
                </span>
                <v-icon v-if="eventFilters.group.content" small class="ml-1">
                  mdi-close
                </v-icon>
              </span>
            </v-card>
            <v-card
              v-if="false"
              class="px-2 py-1 rounded-pill filterPill mr-2"
              flat
              :style="
                eventFilters.action.content
                  ? 'border: 1px dashed #a0acff !important'
                  : 'border: 1px solid #a0acff !important'
              "
              @click="(e) => handleEventFilter(eventFilters.action, e)"
            >
              <span>
                <span v-if="!eventFilters.action.content" class="mr-1">+</span>
                {{ eventFilters.action.title }}
                <span v-if="eventFilters.action.content">
                  <b>{{ eventFilters.action.content }}</b>
                </span>
                <v-icon v-if="eventFilters.action.content" small class="ml-1">
                  mdi-close
                </v-icon>
              </span>
            </v-card>
            <span
              v-if="hasAnySelectedFilter"
              class="mb-1"
              style="color: red"
              @click="clearFilters"
            >
              <small
                >X {{ $t('message.pages.organization.clearFilters') }}</small
              >
            </span>
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-row
            no-gutters
            align="center"
            justify="end"
            v-if="events.length && pagesLimit > 1"
          >
            <span class="mx-2 pointer" @click="handlePreviousPage">
              <v-icon>mdi-chevron-left</v-icon>
            </span>
            <v-card
              flat
              width="70px"
              class="d-flex flex-row align-center justify-center"
            >
              <span>{{ eventRequestFilter.page }} of {{ pagesLimit }}</span>
            </v-card>
            <span class="mx-2 pointer" @click="handleNextPage">
              <v-icon>mdi-chevron-right</v-icon>
            </span>
            <v-card flat>
              <v-row no-gutters align="center" justify="center">
                <span class="mr-3">{{
                  $t('message.pages.organization.goTo')
                }}</span>
                <v-text-field
                  v-model="goToPage"
                  dense
                  outlined
                  style="max-width: 70px"
                  hide-details
                  type="number"
                  @input="handleGoToPage"
                  @keydown.enter="handleGoDirectToPage"
                  class="goToInput"
                ></v-text-field>
              </v-row>
            </v-card>
          </v-row>
        </v-col>
        <v-col class="d-flex align-center justify-start">
          <v-card class="d-flex align-center" height="45px" flat>
            <v-hover v-slot="{ hover }">
              <v-row no-gutters>
                <span
                  class="caption mt-1"
                  style="height: 24px; color: var(--v-blueMarine-base)"
                >
                  <b>Evento</b>
                </span>
                <v-icon v-if="hover && !eventOrder" color="grey lighten-1"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && eventOrder === 'desc'"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && eventOrder === 'asc'">
                  mdi-menu-up
                </v-icon>
              </v-row>
            </v-hover>
          </v-card>
        </v-col>
        <v-card class="d-flex align-center" height="45px" flat width="150px ">
          <v-hover v-slot="{ hover }">
            <v-row no-gutters>
              <span
                class="caption mt-1"
                style="height: 24px; color: var(--v-blueMarine-base)"
              >
                <b> Data e hora </b>
              </span>
              <v-icon v-if="hover && !dateOrder" color="grey lighten-1"
                >mdi-menu-down</v-icon
              >
              <v-icon v-if="hover && dateOrder === 'desc'"
                >mdi-menu-down</v-icon
              >
              <v-icon v-if="hover && dateOrder === 'asc'">mdi-menu-up</v-icon>
            </v-row>
          </v-hover>
        </v-card>
        <v-col cols="12" v-if="loadings.fetchEventsInfo || loadingLogs">
          <v-skeleton-loader type="table-tbody" class="orgLogsSkeletonLoader" />
        </v-col>
        <vue-virtual-table
          v-else-if="events.length"
          class="eventsTable"
          :config="headers"
          :data="events"
          :height="tableHeight"
          :itemHeight="48"
          :minWidth="450"
        >
          <template slot-scope="scope" slot="userCommon">
            <v-col
              class="d-flex align-center justify-start pa-0"
              style="padding-left: 12px !important"
            >
              <span
                :title="scope.row.json.user_name"
                class="text-truncate"
                :style="'max-width: 100%'"
              >
                {{ scope.row.json.user_name ? scope.row.json.user_name : '-' }}
              </span>
            </v-col>
          </template>
          <template slot-scope="scope" slot="event">
            <v-col class="d-flex align-center justify-start pa-0">
              <span
                :title="scope.row.json.user_name"
                class="text-truncate filterPill"
                :style="'max-width: 100%'"
              >
                {{ scope.row.description }}
              </span>
            </v-col>
          </template>
          <template slot-scope="scope" slot="time">
            <v-row no-gutters>
              <span class="filterPill">
                {{ formatDate(scope.row.date) }}
              </span>
            </v-row>
          </template>
        </vue-virtual-table>
        <v-col v-else cols="12">
          <v-container fill-height>
            <v-row
              class="d-flex flex-column"
              no-gutters
              justify="center"
              align="center"
            >
              <span class="mt-16">{{
                $t('message.pages.organization.logMissing')
              }}</span>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
    </template>
  </page-view>
</template>

<script>
// internal imports
import linkHelper from '@/utils/linkHelper.mixin.js'
import { subDays, subMonths, format } from 'date-fns'
import _ from 'lodash'
import calcMegabytesMixin from '@/utils/calcMegabytes.js'

// API
import imagesAPI from '@/services/imagesApi.js'
import organizationApi from '@/services/organizationApi.js'
import planApi from '@/services/planApi'
import invoiceApi from '@/services/invoiceApi'
import subUsersManagement from '@/services/subUsersManagement'

// Components
import VueVirtualTable from 'vue-virtual-table'
import DatePicker from 'vue2-datepicker'

import LoadingLogo from '@/components/LoadingLogo.vue'
import PageView from '@/layout/PageView.vue'
import { mapState } from 'vuex'
import i18n from '@/translations/lang'
import StripeRedirectionModal from './StripeRedirectionModal.vue'

export default {
  name: 'Organization',
  mixins: [linkHelper, calcMegabytesMixin],
  components: {
    DatePicker,
    VueVirtualTable,
    PageView,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
    BaseBtn: () => import('@/components/BaseBtn.vue'),
    LoadingLogo,
    StripeRedirectionModal,
  },
  data: () => ({
    organization: {
      name: '',
      image_path: '',
      image_id: '',
    },
    editing: '',
    loading: false,
    eventFilters: {
      user: {
        objectName: 'user',
        title: i18n.t('message.pages.organization.user'),
        content: '',
      },
      group: {
        objectName: 'group',
        title: i18n.t('message.pages.organization.section'),
        content: '',
      },
      period: {
        objectName: 'period',
        title: i18n.t('message.pages.organization.period'),
        content: '',
      },
      action: {
        objectName: 'action',
        title: i18n.t('message.pages.organization.action'),
        content: '',
      },
    },
    actions: [
      { title: 'create', text: 'Adicionar' },
      { title: 'update', text: 'Alterar' },
      { title: 'remove', text: 'Remover' },
    ],
    groups: [
      {
        title: 'roles',
        text: i18n.t('message.pages.users.categories.roles'),
      },
      { title: 'drm', text: i18n.t('message.pages.drm.watermark') },
      {
        title: 'organizations',
        text: i18n.t('message.pages.organization.title'),
      },
      { title: 'videos', text: i18n.t('message.videos') },
    ],
    eventRequestFilter: {
      page: 1,
      limit: 100,
      group: '',
      user: '',
    },
    events: [],
    showAllEvents: false,
    loadings: {
      fetchData: false,
      fetchEventsInfo: false,
    },
    errors: {
      fetchData: false,
      fetchError: false,
      shownErrMsg: 'Error',
      tooltipErrMsg: 'Error',
    },
    headers: [
      {
        prop: '_action',
        name: 'Evento',
        actionName: 'event',
      },
      {
        prop: '_action',
        name: 'Data e hora',
        actionName: 'time',
        width: 150,
      },
    ],
    userOrder: false,
    eventOrder: false,
    groupOrder: false,
    dateOrder: false,
    logoFile: null,
    isEditingOrganizationName: false,
    isFetchingUpdate: false,
    loadingLogo: false,
    loadingDeleteLogo: false,
    loadingLogs: true,
    stripeLink: '',
    organizationPhotoFile: null,
    showMenu: false,
    x: 0,
    y: 0,
    menuItem: false,
    users: [],
    dateRange: [new Date('2022/01/01'), new Date()],
    totalLogs: 0,
    goToPage: 0,
    filtersTimeout: null,
    isBeta: false,
    crispLoading: false,
  }),
  async created() {
    this.isBeta = location.search.match('beta')
    let params = new URLSearchParams(location.search)
    let unsubscribeToken = params.get('unsubscribe_token')
    this.loadings.fetchData = true
    await this.getOrganization()
    this.handleLogs()
    this.getUsers()
    this.loadings.fetchData = false
    if (unsubscribeToken) {
      this.$root.$emit('openStripeRedirectionModal', unsubscribeToken)
    }
  },
  computed: {
    ...mapState({
      userPlanInfo: (state) => state.user.userPlanInfo,
    }),
    isRootUser() {
      return this.$store.state.user?.userAllInfo?.is_root ?? false
    },
    hasEditOrgPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-organization') ??
        false
      )
    },
    pagesLimit() {
      return Math.ceil(this.totalLogs / this.eventRequestFilter.limit)
    },
    shortcuts() {
      const today = {
        text: i18n.t('message.pages.video.metricsPage.datepicker.today'),
        onClick() {
          const now = new Date()
          const startOfDay = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
          )
          const currentTime = new Date()
          return [startOfDay, currentTime]
        },
      }
      const days = [
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.yesterday'),
          onClick() {
            const date = new Date()
            return [subDays(date, 1), date]
          },
        },
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.7days'),
          onClick() {
            const date = new Date()
            return [subDays(date, 7), date]
          },
        },
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.aMonth'),
          onClick() {
            const date = new Date()

            return [subMonths(date, 1), date]
          },
        },
      ]
      return [today, ...days]
    },
    dateRangeValue() {
      const mask = (date) => format(date, 'dd/MM/yyyy')
      const [start, end] = _.isArray(this.dateRange)
        ? this.dateRange
        : [this.dateRange, this.dateRange]

      return `${mask(start)} ~ ${mask(end)}`
    },
    menuWidthOnMenuItem() {
      if (this.menuItem.objectName === 'action') {
        return '150px'
      } else {
        return '300px'
      }
    },
    hasAnySelectedFilter() {
      let hasAny = false
      Object.keys(this.eventFilters).forEach((filter) => {
        if (filter.content) hasAny = true
      })
      return hasAny
    },
    canSaveName() {
      return this.organization.name.length > 2
    },
    hasAvatar() {
      return !!this.organization.image_id && !!this.organization.image_path
    },
    tableHeight() {
      if (this.events.length < 5) {
        return 288
      } else if (this.events.length <= 100) {
        return (this.events.length + 1) * 48 + 5
      } else {
        return 4805
      }
    },
    menu() {
      return this.$store.state.user.settingsNavItems
    },
    userNameInitials() {
      let initials = this.organization.name
      if (initials) {
        return initials
          .match(/(^\S\S?|\s\S)?/g)
          .map((v) => v.trim())
          .join('')
          .match(/(^\S|\S$)?/g)
          .join('')
          .toLocaleUpperCase()
      }
      return initials
    },
    reducedEvents() {
      if (this.events) {
        if (this.events.length > 4) {
          return this.events.slice(0, 5)
        } else {
          return this.events
        }
      } else {
        return []
      }
    },
  },
  watch: {
    eventRequestFilter: {
      handler: function () {
        this.filtersTimeout && clearTimeout(this.filtersTimeout)
        this.filtersTimeout = setTimeout(() => {
          this.handleInsideLogs()
        }, 350)
      },
      deep: true,
    },
    dateRange: {
      handler: async function () {
        this.loadings.fetchEventsInfo = true
        await this.getLogs()
        this.loadings.fetchEventsInfo = false
      },
    },
    pagesLimit: {
      handler: function (val) {
        if (val < this.eventRequestFilter.page) {
          this.eventRequestFilter.page = val
        }
      },
    },
  },
  mounted() {
    this.resetDateRange()
  },
  methods: {
    handleGoToPage() {
      if (Number(this.goToPage) > Number(this.pagesLimit)) {
        let val = this.pagesLimit
        this.goToPage = val
      }
    },
    handleGoDirectToPage() {
      const val = Number(this.goToPage)
      if (val > this.pagesLimit) {
        this.goToPage = this.pagesLimit
        this.eventRequestFilter.page = this.pagesLimit
      } else {
        this.eventRequestFilter.page = val
      }
    },
    handleNextPage() {
      if (this.eventRequestFilter.page >= this.pagesLimit) return
      this.eventRequestFilter.page += 1
    },
    handlePreviousPage() {
      if (
        this.eventRequestFilter.page <= 1 ||
        this.eventRequestFilter.page > this.pagesLimit
      )
        return
      this.eventRequestFilter.page -= 1
    },
    handleSelectUser(user) {
      this.eventFilters[`${this.menuItem.objectName}`].content = user.name
      this.eventRequestFilter[`${this.menuItem.objectName}`] = user.id
      this.showMenu = false
    },
    handleSelectAction(action) {
      this.eventFilters[`${this.menuItem.objectName}`].content = action.title
      this.showMenu = false
    },
    handleSelectGroup(group) {
      this.eventFilters[`${this.menuItem.objectName}`].content = group.text
      this.eventRequestFilter[`${this.menuItem.objectName}`] = group.title
      this.showMenu = false
    },
    async handleLogs() {
      this.loadingLogs = true
      await this.getLogs()
      this.loadingLogs = false
    },
    async handleInsideLogs() {
      this.loadings.fetchEventsInfo = true
      await this.getLogs()
      clearTimeout(this.filtersTimeout)
      this.loadings.fetchEventsInfo = false
    },
    async getLogs() {
      let filters = {
        page: this.eventRequestFilter.page,
        limit: this.eventRequestFilter.limit,
        group: this.eventRequestFilter.group,
        user_id: this.eventRequestFilter.user,
        start_date: new Date(this.dateRange[0]).toISOString(),
        end_date: new Date(this.dateRange[1]).toISOString(),
      }
      try {
        let ret = await organizationApi.getLogs(filters)
        if (!ret.data?.logs) {
          this.events = []
        } else {
          this.events = ret.data.logs.map((log) => {
            return { ...log, description: this.createADescription(log.json) }
          })
        }
        this.totalLogs = ret.data?.total ?? 0
        if (
          this.goToPage >
          Math.round(this.totalLogs / this.eventRequestFilter.limit)
        ) {
          this.getLogs()
        }
      } catch (e) {
        console.log('e', e)
      }
    },
    createADescription(event) {
      const { user_name, action, group } = event
      const actionMap = {
        create: i18n.t('message.pages.organization.create'),
        update: i18n.t('message.pages.organization.update'),
        delete: i18n.t('message.pages.organization.delete'),
      }
      const actionMapped = actionMap[action]

      const groupActionMap = {
        video: i18n.t('message.pages.organization.aVideo'),
        role: i18n.t('message.pages.organization.aRole'),
        user: i18n.t('message.pages.organization.anUser'),
      }
      const groupMapped = groupActionMap[group]

      return `${user_name} ${actionMapped ?? action} ${groupMapped ?? group}.`
    },
    handleEventFilter(filter, e) {
      this.menuItem = filter
      if (filter.content) {
        this.eventRequestFilter[`${this.menuItem.objectName}`] = ''
        this.eventFilters[`${filter.objectName}`].content = ''
      } else {
        e.preventDefault()
        this.showMenu = false
        this.x = e.clientX
        this.y = e.clientY
        this.$nextTick(() => {
          this.showMenu = true
        })
      }
    },
    clearFilters() {
      this.eventFilters.forEach((filter, i) => {
        this.eventFilters[i].content = ''
      })
      this.eventRequestFilter.group = ''
    },
    async getUsers() {
      try {
        let ret = await subUsersManagement.getAllsubUsers()
        this.users = ret.data
      } catch (e) {
        console.log('e', e)
      }
    },
    async fetchInvoices() {
      try {
        let ret = await invoiceApi.getInvoices()
        const invoices = JSON.parse(JSON.stringify(ret.data.invoices))
        const totalAmountPaid = invoices.reduce((accumulator, currentValue) => {
          return accumulator + currentValue.amount_paid
        }, 0)
        return (totalAmountPaid / 100).toFixed(2)
      } catch (e) {
        console.log('e', e)
      }
    },
    async formatPlanInfo(userInfo, planInfo) {
      let totalLength = 0
      if (window.db?.videos) {
        totalLength = await window.db.videos.count()
      }
      const [invoiceValue, userPaidValue] = await Promise.all([
        this.getThisCycleInfoWithProrate(),
        this.fetchInvoices(),
      ])

      let usedTraffic = String(
        this.calcMegabytes(planInfo?.limit?.traffic, 'MB', 'GB', true)
      ).replace(',', '.')
      const planValue = (planInfo.subscription.unit_amount / 100).toFixed(2)
      const nowValue = this.formatDate(Date.now())

      let infoArray = [
        ['Atualizado_em', `${nowValue}`],
        ['Data_pedido_cancelamento', `${nowValue}`],
        ['Nome', `${userInfo.name}`],
        ['Email', `${userInfo.email}`],
        ['Telefone', `${userInfo.phone}`],
        ['Preco_plano_atual', `${planValue}`],
        [
          'Periodo_atual',
          `${this.formatDate(
            planInfo.limit.start_period
          )} até ${this.formatDate(planInfo.limit.end_period)}`,
        ],
        ['Status_assinatura', `${planInfo.subscription.status}`],
        ['Criado_em', `${this.formatDate(userInfo.created_at)}`],
        ['Banda_utilizada', `${usedTraffic}`],
        [
          'Videos_plataforma',
          `${totalLength > 0 ? totalLength : 'Não identificado'}`,
        ],
        ['Valor_invoice_atual', `${invoiceValue ?? 'Não identificado'}`],
        ['Valor_pago_acumulado', `${userPaidValue ?? 'Não identificado'}`],
        ['currency', `${planInfo.currency ?? 'Não identificado'}`],
        [
          'Cargo',
          `${planInfo.customer_metadata.client_role ?? 'Não identificado'}`,
        ],
        [
          'Tamanho_da_equipe',
          `${
            planInfo.customer_metadata.client_team_size ?? 'Não identificado'
          }`,
        ],
        [
          'Razao_da_conta',
          `${
            planInfo.customer_metadata.client_reasoning ?? 'Não identificado'
          }`,
        ],
      ]

      return infoArray
    },
    getLocale() {
      if (this.$i18next.resolvedLanguage === 'en') return 'en-US'
      return 'pt-BR'
    },
    async getThisCycleInfoWithProrate() {
      let items = []
      try {
        let ret = await planApi.previewInvoice(
          this.userPlanInfo.subscription_id,
          this.getLocale().toLowerCase(),
          items
        )
        return ret.data.amount_due / 100
      } catch (error) {
        console.error('error', error)
      }
    },
    async goToStripe() {
      this.crispLoading = true
      if (typeof this.$crisp !== 'undefined') {
        let planInfoText = await this.formatPlanInfo(
          this.$store.state.user.userAllInfo,
          this.$store.state.user.userPlanInfo
        )
        this.$crisp.push(['do', 'chat:show'])
        this.$crisp.push(['do', 'chat:open'])
        // Envia a mensagem predefinida
        this.$crisp.push([
          'on',
          'chat:opened',
          this.$crisp.push(['set', 'session:data', [planInfoText]]),
          this.$crisp.push([
            'do',
            'message:send',
            ['text', i18n.t('message.pages.organization.cancel_info')],
          ]),
        ])
      }
      this.crispLoading = false
    },

    async getStripe(token) {
      try {
        const { data } = await planApi.getReturnUrl(
          'https://dashboard.pandavideo.com.br',
          'pt-BR',
          token
        )
        window.open(data.url, '_blank')
      } catch (error) {
        this.handleFetchError(error)
      }
    },
    handleFetchError(err) {
      this.errors.shownErrMsg = i18n.t('message.errors.unexpectedError')
      let errString = ''
      if (
        err?.response?.data &&
        err?.response?.data?.errMsg &&
        err?.response?.data?.errCode
      ) {
        errString = err.response.data.errCode + err.response.data.errMsg
      }
      this.errors.fetchError = true
      this.errors.tooltipErrMsg =
        err.msg ?? errString ?? err.response.data.message ?? 'Error'
    },
    resetDateRange() {
      // Get the current date
      const today = new Date()
      // Calculate the date 24 hours before now
      const previousDay = new Date(today.getTime() - 24 * 60 * 60 * 1000)
      // Set the first date as 24 hours before now (00:00:00)
      const startOfDay = new Date(
        previousDay.getFullYear(),
        previousDay.getMonth(),
        previousDay.getDate()
      )
      // Set the second date as the current moment
      const endOfDay = new Date()
      if (
        startOfDay.getTime() === this.dateRange[0].getTime() &&
        endOfDay.getTime() === this.dateRange[1].getTime()
      ) {
        return
      }
      this.dateRange = [startOfDay, endOfDay]
    },
    formatDate(timestamp) {
      const date = new Date(timestamp)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${day}/${month}/${year} - ${hours}h${minutes}`
    },
    verifyDisabledDate(date) {
      const today2 = new Date()
      today2.setHours(0, 0, 0, 0)
      if (date > today2) {
        return date > today2
      }
      if (this.dateRange.length > 0) {
        const firstPick = this.dateRange[0]

        // Calculate the range start date
        const rangeStartDate = new Date(firstPick)
        rangeStartDate.setDate(rangeStartDate.getDate() - 29)

        // Calculate the range end date
        const rangeEndDate = new Date(firstPick)
        rangeEndDate.setDate(rangeEndDate.getDate() + 29)

        // Disable dates outside the range
        return date < rangeStartDate || date > rangeEndDate
      }
      return false
    },
    async getOrganization() {
      try {
        let ret = await organizationApi.getOrganization()
        this.organization = {
          name: ret.data.name,
          image_path: ret.data.image_path,
          image_id: ret.data.image_id,
        }
      } catch (e) {
        console.log('e', e)
      }
    },
    async handleUpdateOrganization(onlyName) {
      this.isFetchingUpdate = true
      try {
        let dataObject = {
          name: this.organization.name,
        }
        if (!onlyName) {
          dataObject['image_id'] = this.organization.image_id
        }
        let ret = await organizationApi.updateOrganization(dataObject)
        this.$root.$emit('updateToolbarOrganization', ret.data)
        this.organization = ret.data
        this.isFetchingUpdate = false
        this.isEditingOrganizationName = false
      } catch (e) {
        this.isFetchingUpdate = false
        console.log('e', e)
      }
    },
    handleClearInput() {
      this.organization.name = ''
    },
    async deleteImage() {
      if (this.organization.image_id) {
        this.loading = true
        await organizationApi.updateOrganization({ image_id: null })
        await imagesAPI.deleteImage(this.organization.image_id)
        this.organization.image_path = null
        this.organization.image_id = null
        this.loading = false
      }
    },
    async handleUpload(file) {
      this.organizationPhotoFile = file
      await this.saveImage()
    },
    async saveImage() {
      this.loading = true
      if (this.organization.image_path) {
        await this.deleteImage()
      }
      const data = new FormData()
      data.append('file', this.organizationPhotoFile)
      var reader = new FileReader()
      reader.readAsDataURL(this.organizationPhotoFile)
      reader.onload = async () => {
        const options = [
          {
            filename: this.organizationPhotoFile.name,
            type: 'template',
          },
        ]
        const data = await fetch(reader.result)
        const blobData = await data.blob()
        let ret = await imagesAPI.handleUploadImage(blobData, options)
        this.organization.imagePath = `${ret.cdn_base}/${ret.storage_path}`
        this.organization.image_id = ret.id
        this.handleUpdateOrganization()
      }
      this.loading = false
    },
    handleOpenFilter() {
      this.$root.$emit('openEventsFilter')
    },
    handleShowAllEvents() {
      this.showAllEvents = true
    },
    handleLeaveFilters() {
      this.showAllEvents = false
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
    },
  },
}
</script>

<style lang="scss" scoped>
.hidden {
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;

  width: 0.1px;
  height: 0.1px;
  overflow: hidden;
  opacity: 0;
}

.grid {
  display: grid;

  &__avatar {
    $size: 130px;

    position: relative;
    margin: 0 auto 50px;

    display: flex;
    width: $size;
    height: $size;
    border: 4px solid var(--v-neutral-lighten3);
    border-radius: 100%;

    align-items: center;
    justify-content: center;
    overflow: hidden;

    &--text {
      background-color: var(--v-brand-lighten1);

      font-size: 2rem;
      color: #fff;
    }

    &--ctrl {
      display: none;
      background: rgba(0, 0, 0, 0.8);

      &::v-deep {
        .v-divider {
          $size: 60%;

          margin: 20% 0;

          border-color: rgba(#fff, 0.4);
          max-height: $size;
          min-height: $size;
        }

        label {
          padding: 0.75rem;
          cursor: pointer;
        }

        .base-button {
          $color: rgba(#000, 0);

          background-color: $color !important;
          border-color: $color !important;
        }

        .mdi-camera {
          color: white;
        }

        .mdi-delete {
          color: #ffcdd2;
        }
      }
    }

    &:hover {
      [class*='--ctrl'] {
        position: absolute;
        top: 0;
        right: 0;

        display: flex;
        width: 100%;
        height: 100%;

        align-items: center;
        justify-content: center;
      }
    }
  }

  &__options {
    font-size: 1rem;
    color: var(--v-neutral-base);

    > .d-flex {
      min-height: 50px;
      align-items: stretch;

      &:not(:last-child) {
        box-shadow: inset 0px -1px 0px rgba(0, 0, 0, 0.12);
      }

      > div {
        display: flex;
        align-items: center;
      }
    }

    .edit {
      display: flex;
      align-items: center;

      cursor: pointer;

      .v-icon {
        margin-left: $spacing-minus-2;
        opacity: 0;
      }

      &:hover {
        color: var(--v-neutral-lighten1);

        .v-icon {
          opacity: 1;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.bg-dark {
  .grid {
    &__options {
      color: var(--v-neutral-lighten2);

      .edit:hover {
        color: var(--v-neutral-lighten3);
      }
    }

    &__footer {
      .theme--dark.v-input input {
        color: var(--v-neutral-base) !important;
      }
    }
  }
}
</style>

<style lang="scss">
@import '~vue2-datepicker/scss/index.scss';
.pageTitle {
  font-weight: 500;
  font-size: 18px;
  line-height: 21px;
  color: var(--v-brand-darken2);
}
.categoriesTitle {
  font-weight: 500;
  font-size: 16px;
  line-height: 150%;
  color: var(--v-brand-darken2);
}
.categoriesContext {
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
}
.paymentItem {
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
}
.mainSubject {
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
}
.adviceOrganizationName {
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
}
.subjectInfo {
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
}
.eventsTable .header-cell {
  justify-content: start !important;
  padding-left: 20px !important;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
}

.eventsTable .item-cell {
  border-bottom: 0px !important;
  border-top: 1px solid var(--v-contrast-base);
  padding: 0px !important;
}

.eventsTable {
  overflow-x: hidden !important;
  background-color: var(--v-dark-newBackground);
  border: none !important;
}

.eventsTable .t-header {
  display: none;
}
.bg-dark .eventsTable .item-line {
  background-color: var(--v-neutral-darken2) !important;
}
.filterPill {
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
}
.card-thumbnail {
  border-style: solid;
  border-color: rgba(0, 0, 0, 0.08) !important;
}

.card-thumbnail .v-icon.v-icon::after {
  background-color: transparent !important;
}

.card-thumbnail .v-icon {
  width: 150px !important;
  height: 83px !important;
  z-index: 2;
}

.card-thumbnail .v-icon:before {
  margin-top: -25px !important;
}

.card-thumbnail .v-input__icon {
  width: 150px !important;
}

.card-thumbnail .v-input {
  padding: 0 !important;
}

.card-thumbnail .v-input__prepend-outer {
  margin-right: 0 !important;
}
.listItem {
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
}
.organizationCalendar {
  .mx-datepicker-btn-confirm {
    background-color: var(--v-brand-base);
    color: var(--v-neutral-lighten4);

    &:hover {
      background-color: var(--v-brand-darken1);
      border-color: var(--v-brand-darken1);
    }

    &:active {
      background-color: var(--v-brand-darken2);
      border-color: var(--v-brand-darken2);
    }
  }
}
.mx-datepicker-range {
  max-width: 295px;
  width: 295px !important;
}
.pointer {
  cursor: pointer;
}
.goToInput {
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
.orgLogsSkeletonLoader .v-skeleton-loader__table-tbody {
  padding: 0px !important;
}

.bg-dark,
.theme--dark {
  .pageTitle,
  .categoriesTitle {
    color: #8fb1e3;
  }

  .organizationCalendar {
    .mx-datepicker-btn-confirm {
      &:hover {
        background-color: #70a5f2;
        border-color: #70a5f2;
      }

      &:active {
        background-color: #8fb1e3;
        border-color: #8fb1e3;
      }
    }
  }
}
</style>
